/**
 * Modal Component - 공통 모달 (헤더 / [서브헤더] / 바디 / 푸터)
 * ReactDOM.createPortal 사용 → document.body에 직접 마운트
 * 헤더를 드래그해서 이동 가능
 */

import { CSSProperties, HTMLAttributes, ReactNode, Ref, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  subHeader?: ReactNode;
  headerActions?: ReactNode;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
  showClose?: boolean;
  closeOnOverlay?: boolean;
  closeOnEsc?: boolean;
  className?: string;
  bodyClassName?: string;
  style?: CSSProperties;
  headerProps?: HTMLAttributes<HTMLDivElement>;
  dialogRef?: Ref<HTMLDivElement>;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  subHeader,
  headerActions,
  footerLeft,
  footerRight,
  showClose = true,
  closeOnOverlay = true,
  closeOnEsc = true,
  className = '',
  bodyClassName = '',
  style,
  headerProps,
  dialogRef,
}: ModalProps) {
  // ── 드래그 상태 ────────────────────────────────────────────────────────────
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const startRef = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  // 모달이 열릴 때마다 위치 초기화
  useEffect(() => {
    if (isOpen) setPos({ x: 0, y: 0 });
  }, [isOpen]);

  const handleHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // 버튼 클릭이나 입력 요소는 드래그 제외
    if ((e.target as HTMLElement).closest('button, input, textarea, select, a')) return;
    e.preventDefault();
    dragging.current = true;
    startRef.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };

    const onMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      setPos({
        x: startRef.current.px + ev.clientX - startRef.current.mx,
        y: startRef.current.py + ev.clientY - startRef.current.my,
      });
    };
    const onUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── ESC / 스크롤 잠금 ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (closeOnEsc && e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, closeOnEsc]);

  if (!isOpen) return null;

  const { className: headerClassName = '', ...restHeaderProps } = headerProps ?? {};

  const dialogStyle: CSSProperties = {
    ...style,
    ...(pos.x !== 0 || pos.y !== 0
      ? { transform: `translate(${pos.x}px, ${pos.y}px)` }
      : {}),
  };

  return createPortal(
    <div
      className="dialog-overlay"
      onMouseDown={(e) => {
        e.stopPropagation();
        e.currentTarget.dataset.downOutside = e.target === e.currentTarget ? '1' : '';
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (closeOnOverlay && e.target === e.currentTarget && e.currentTarget.dataset.downOutside) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={`dialog dialog--${size} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 — 드래그 핸들 */}
        <div
          className={`dialog-header ${headerClassName}`.trim()}
          style={{ cursor: 'grab', userSelect: 'none' }}
          onMouseDown={handleHeaderMouseDown}
          {...restHeaderProps}
        >
          <div className="dialog-title">{title}</div>
          {headerActions && <div className="dialog-header-actions">{headerActions}</div>}
          {showClose && (
            <button type="button" className="dialog-close" onClick={onClose} aria-label="닫기">✕</button>
          )}
        </div>

        {subHeader && <div className="dialog-subheader">{subHeader}</div>}

        <div className={`dialog-body ${bodyClassName}`.trim()}>{children}</div>

        {(footerLeft || footerRight) && (
          <div className="dialog-footer">
            {footerLeft && <div className="dialog-footer-left">{footerLeft}</div>}
            {footerRight && <div className="dialog-footer-right">{footerRight}</div>}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default Modal;
